import React, { useEffect, useState, useCallback } from "react";
import {
  adminListTickets,
  adminPatchTicket,
  adminReplyTicket,
  adminTicketDetail,
  adminTicketStats,
  adminTicketCannedReplies,
  adminClaimTicket,
  adminReleaseTicket,
  adminReopenTicket,
} from "../../lib/api";

/**
 * TicketsSection — admin support-ticket queue with master-detail layout.
 *
 * Left pane: filterable / searchable list of tickets with status counts,
 *            SLA chips and claim ownership.
 * Right pane: full ticket detail + player context + canned-reply picker +
 *            reply log + claim/reopen/close controls.
 *
 * Replies are sent as DMs (never in-thread). All side-effects round-trip
 * through `adminReplyTicket`, which appends to the ticket's `reply_log`
 * server-side so the audit trail survives reopens.
 */
const CATEGORY_OPTIONS = ["all", "bug", "billing", "abuse", "account", "feature", "other"];
const SORT_OPTIONS = [
  { value: "newest",   label: "Newest" },
  { value: "oldest",   label: "Oldest" },
  { value: "updated",  label: "Recently updated" },
  { value: "priority", label: "Priority (bug/billing first)" },
];

function SlaBadge({ sla, status }) {
  if (!sla || status === "answered" || status === "closed") {
    return (
      <span className="text-[9px] tracking-widest px-2 py-0.5 rounded-full bg-white/8 border border-white/15 text-white/55">
        —
      </span>
    );
  }
  if (sla.within_sla === false) {
    return (
      <span className="text-[9px] tracking-widest px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-400/45 text-rose-300">
        BREACHED
      </span>
    );
  }
  const hrs = sla.hours_remaining;
  const colour =
    hrs < 2  ? "bg-amber-500/15 border-amber-400/45 text-amber-300" :
    hrs < 8  ? "bg-yellow-500/15 border-yellow-400/45 text-yellow-300" :
    "bg-[#14b8a6]/15 border-[#14b8a6]/45 text-[#14b8a6]";
  return (
    <span className={`text-[9px] tracking-widest px-2 py-0.5 rounded-full border ${colour}`}>
      {hrs < 1 ? `${Math.round(hrs * 60)}m` : `${Math.round(hrs)}h`} left
    </span>
  );
}

function StatsHeader({ stats }) {
  if (!stats) return null;
  const fmt = (n) => (n == null ? "—" : n);
  return (
    <div
      data-testid="admin-ticket-stats"
      className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4"
    >
      {[
        ["OPEN", stats.counts?.open, "text-[#14b8a6]"],
        ["IN PROGRESS", stats.counts?.in_progress, "text-yellow-300"],
        ["SLA BREACHES", stats.sla_breaches, "text-rose-300"],
        ["AVG RESPONSE", stats.avg_response_hours != null
          ? `${stats.avg_response_hours}h` : "—", "text-violet-300"],
      ].map(([label, val, cls]) => (
        <div key={label} className="glass rounded-2xl p-3">
          <p className="text-[9px] tracking-widest text-white/45">{label}</p>
          <p className={`text-xl font-bold mt-1 ${cls}`}>{fmt(val)}</p>
        </div>
      ))}
    </div>
  );
}

function TicketRow({ ticket, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`admin-ticket-row-${ticket.ticket_id}`}
      className={`w-full text-left p-3 rounded-2xl border transition ${
        active
          ? "bg-[#14b8a6]/12 border-[#14b8a6]/60"
          : "bg-black/30 border-white/10 hover:border-[#14b8a6]/40"
      }`}
    >
      <div className="flex justify-between gap-2">
        <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold uppercase">
          {ticket.category}
        </p>
        <SlaBadge sla={ticket.sla_status} status={ticket.status} />
      </div>
      <p className="font-bold text-sm mt-1 truncate">{ticket.subject}</p>
      <p className="text-[11px] text-white/55 truncate">
        {ticket.user_name || ticket.user_id}
      </p>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[9px] tracking-widest text-white/40">
          {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : ""}
        </span>
        {ticket.claimed_by_admin_handle && (
          <span className="text-[9px] tracking-widest px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-400/30">
            @{ticket.claimed_by_admin_handle}
          </span>
        )}
      </div>
    </button>
  );
}

function PlayerContext({ context }) {
  if (!context?.user) return null;
  const u = context.user;
  const isPro = u.user_type === "pro" || u.paid_pro_active;
  const reportCount = context.report_count || 0;
  const reportClass =
    reportCount > 5 ? "text-rose-300 font-bold" :
    reportCount > 2 ? "text-amber-300" : "";
  return (
    <div data-testid="admin-ticket-player-context" className="space-y-2 text-[11px]">
      <p className="text-[10px] tracking-widest text-white/45 font-bold">
        <i className="fas fa-user mr-1" />PLAYER CONTEXT
      </p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <span className="text-white/45">Tier</span>
        <span className={isPro ? "text-[#14b8a6] font-bold" : ""}>
          {isPro ? "PRO" : "CASUAL"}
          {u.suspended ? " · SUSPENDED" : ""}
        </span>
        <span className="text-white/45">Stripe customer</span>
        <span className="font-mono text-[10px]">
          {u.stripe_customer_id || "—"}
        </span>
        <span className="text-white/45">Joined</span>
        <span>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</span>
        <span className="text-white/45">Total tickets</span>
        <span>{context.ticket_count}</span>
        <span className="text-white/45">Reports filed against</span>
        <span data-testid="admin-ticket-report-count" className={reportClass}>
          {reportCount}{reportCount > 5 ? " ⚠" : ""}
        </span>
        <span className="text-white/45">Wins / Losses</span>
        <span>{u.wins || 0} / {u.losses || 0}</span>
      </div>
      {context.last_challenge && (
        <p className="text-[10px] text-white/45 mt-2">
          Last match: {context.last_challenge.challenge_id} ·
          {" "}{context.last_challenge.score_a}–{context.last_challenge.score_b}
        </p>
      )}
    </div>
  );
}

function ReplyLog({ entries }) {
  if (!entries?.length) return null;
  return (
    <div data-testid="admin-ticket-reply-log" className="mt-4">
      <p className="text-[10px] tracking-widest text-white/45 font-bold mb-2">
        <i className="fas fa-clock-rotate-left mr-1" />REPLY LOG ({entries.length})
      </p>
      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
        {entries.map((e) => (
          <div key={e.message_id}
               className="p-2 rounded-lg bg-black/30 border border-white/8 text-[11px]">
            <p className="text-[9px] tracking-widest text-violet-300">
              {new Date(e.created_at).toLocaleString()} ·
              {" "}@{e.admin_handle || "admin"}
            </p>
            <p className="text-white/75 mt-1 whitespace-pre-wrap">{e.body || e.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TicketDetailPane({
  ticket, context, replyLog, canned,
  onReply, onStatus, onClaim, onRelease, onReopen, onSaveNote,
  busy, adminHandle, setAdminHandle,
}) {
  const [draft, setDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);

  // Reset draft when the active ticket changes so we don't accidentally
  // send the previous ticket's text against a new ticket.
  useEffect(() => {
    setDraft("");
    setNoteDraft(ticket?.admin_note || "");
    setNoteOpen(false);
  }, [ticket?.ticket_id, ticket?.admin_note]);

  if (!ticket) {
    return (
      <div data-testid="admin-ticket-detail-empty"
           className="glass rounded-2xl p-10 text-center text-white/45 text-sm">
        Select a ticket from the queue to view details.
      </div>
    );
  }

  const closed = ticket.status === "closed";
  const claimed = !!ticket.claimed_by_admin_handle;

  return (
    <div data-testid="admin-ticket-detail" className="glass rounded-2xl p-5 space-y-4">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold uppercase">
            {ticket.category} · {ticket.ticket_id}
          </p>
          <h3 className="font-bold text-lg">{ticket.subject}</h3>
          <p className="text-[11px] text-white/60">
            {ticket.user_name || ticket.user_id}
            {ticket.user_email ? ` · ${ticket.user_email}` : ""}
            {" · "}
            {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : ""}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-[9px] tracking-widest px-2 py-0.5 rounded-full bg-[#14b8a6]/15 border border-[#14b8a6]/40 text-[#14b8a6]">
            {(ticket.status || "open").toUpperCase()}
          </span>
          <SlaBadge sla={ticket.sla_status} status={ticket.status} />
        </div>
      </div>

      <p className="p-3 rounded-lg bg-black/40 border border-white/10 text-[12px] text-white/80 whitespace-pre-wrap">
        {ticket.body}
      </p>

      <PlayerContext context={context} />

      {/* Internal admin note — never shown to the player, persists across
          reopens. Audit-only context for the next operator on this ticket. */}
      <div data-testid="admin-ticket-note-section" className="space-y-2">
        <button
          type="button"
          onClick={() => setNoteOpen((o) => !o)}
          data-testid="admin-ticket-note-toggle"
          className="text-[10px] tracking-widest text-violet-300 hover:text-violet-200"
        >
          <i className="fas fa-sticky-note mr-1" />
          INTERNAL NOTE {ticket.admin_note ? "· SET" : "· (none)"}
          {" "}{noteOpen ? "▴" : "▾"}
        </button>
        {!noteOpen && ticket.admin_note && (
          <p data-testid="admin-ticket-note-display"
             className="text-[11px] text-amber-200/85 italic px-2 py-1 rounded bg-amber-500/10 border border-amber-400/25">
            {ticket.admin_note}
          </p>
        )}
        {noteOpen && (
          <div className="space-y-2">
            <textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Operator-only note (never shown to player)…"
              data-testid="admin-ticket-note-textarea"
              className="w-full bg-black/40 border border-amber-400/30 rounded-xl px-3 py-2 text-white text-[12px]"
            />
            <button
              type="button"
              onClick={() => onSaveNote(noteDraft).then(() => setNoteOpen(false))}
              disabled={busy}
              data-testid="admin-ticket-note-save-btn"
              className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost"
            >
              {busy ? "…" : "SAVE NOTE"}
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {!claimed ? (
          <button
            type="button"
            onClick={onClaim}
            disabled={busy}
            data-testid="admin-ticket-claim-btn"
            className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost"
          >
            <i className="fas fa-hand-paper mr-1" />CLAIM
          </button>
        ) : (
          <button
            type="button"
            onClick={onRelease}
            disabled={busy}
            data-testid="admin-ticket-release-btn"
            className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost"
          >
            <i className="fas fa-rotate-left mr-1" />RELEASE @{ticket.claimed_by_admin_handle}
          </button>
        )}
        {ticket.status === "open" && (
          <button
            type="button"
            onClick={() => onStatus("in_progress")}
            disabled={busy}
            className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost"
          >
            Mark in progress
          </button>
        )}
        {!closed && (
          <button
            type="button"
            onClick={() => onStatus("closed")}
            disabled={busy}
            data-testid="admin-ticket-close-btn"
            className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost"
          >
            Close
          </button>
        )}
        {closed && (
          <button
            type="button"
            onClick={onReopen}
            disabled={busy}
            data-testid="admin-ticket-reopen-btn"
            className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost"
          >
            <i className="fas fa-rotate-right mr-1" />Reopen
          </button>
        )}
      </div>

      <ReplyLog entries={replyLog} />

      {!closed && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5" data-testid="admin-ticket-canned-row">
            {canned.map((c, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setDraft((d) => (d ? `${d}\n\n${c.body}` : c.body))}
                data-testid={`admin-ticket-canned-${i}`}
                className="text-[10px] tracking-widest px-2.5 py-1 rounded-lg bg-violet-500/12 border border-violet-400/30 text-violet-200 hover:bg-violet-500/20"
              >
                {c.title}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={adminHandle}
            onChange={(e) => setAdminHandle(e.target.value)}
            placeholder="Your handle (shown to the player)"
            data-testid="admin-ticket-handle-input"
            className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-[12px]"
          />
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={5}
            maxLength={1500}
            placeholder="Reply text (sent as DM to player)…"
            data-testid="admin-ticket-reply-textarea"
            className="w-full bg-black/40 border border-[#14b8a6]/40 rounded-xl px-3 py-2 text-white text-sm"
          />
          <button
            type="button"
            onClick={() => onReply(draft).then(() => setDraft(""))}
            disabled={busy || !draft.trim()}
            data-testid="admin-ticket-reply-send-btn"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-60"
          >
            {busy ? "…" : "SEND DM REPLY"}
          </button>
          <p className="text-[10px] text-white/40">
            Replies are delivered as DMs — never in-thread. The player gets a
            push notification + inbox ping.
          </p>
        </div>
      )}
    </div>
  );
}

export function TicketsSection({ onToast }) {
  const [status, setStatus] = useState("open");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("newest");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({
    tickets: [],
    counts: { open: 0, in_progress: 0, answered: 0, closed: 0 },
  });
  const [stats, setStats] = useState(null);
  const [canned, setCanned] = useState([]);
  const [adminHandle, setAdminHandle] = useState(() =>
    localStorage.getItem("lh_support_admin_handle") || "");
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    localStorage.setItem("lh_support_admin_handle", adminHandle);
  }, [adminHandle]);

  const reload = useCallback(async () => {
    const params = { status, sort, limit: 100 };
    if (category && category !== "all") params.category = category;
    if (query.trim()) params.q = query.trim();
    try {
      const [list, statsRow] = await Promise.all([
        adminListTickets(params),
        adminTicketStats().catch(() => null),
      ]);
      setData(list);
      if (statsRow) setStats(statsRow);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load tickets.");
    }
  }, [status, category, sort, query, onToast]);

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [status, category, sort]);
  useEffect(() => {
    const t = setTimeout(reload, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [query]);

  useEffect(() => {
    adminTicketCannedReplies().then((r) => setCanned(r.replies || [])).catch(() => {});
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      setDetail(await adminTicketDetail(id));
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load ticket detail.");
    }
  }, [onToast]);

  useEffect(() => { loadDetail(activeId); }, [activeId, loadDetail]);

  const wrap = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) onToast?.(msg);
      await Promise.all([reload(), loadDetail(activeId)]);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Action failed.");
    } finally { setBusy(false); }
  };

  const onReply = (message) => wrap(
    () => adminReplyTicket(activeId, message, adminHandle || "admin"),
    "Reply sent via DM.",
  );
  const onStatus = (s) => wrap(
    () => adminPatchTicket(activeId, { status: s }),
    `Ticket marked ${s}.`,
  );
  const onClaim = () => wrap(
    () => adminClaimTicket(activeId, adminHandle || "admin"),
    "Ticket claimed.",
  );
  const onRelease = () => wrap(
    () => adminReleaseTicket(activeId),
    "Ticket released.",
  );
  const onReopen = () => wrap(
    () => adminReopenTicket(activeId),
    "Ticket reopened.",
  );
  const onSaveNote = (admin_note) => wrap(
    () => adminPatchTicket(activeId, { admin_note: admin_note || "" }),
    "Internal note saved.",
  );

  const tabs = [
    { id: "open",        label: `OPEN · ${data.counts?.open || 0}` },
    { id: "in_progress", label: `IN PROGRESS · ${data.counts?.in_progress || 0}` },
    { id: "answered",    label: `ANSWERED · ${data.counts?.answered || 0}` },
    { id: "closed",      label: `CLOSED · ${data.counts?.closed || 0}` },
    { id: "all",         label: "ALL" },
  ];

  return (
    <div data-testid="admin-tickets-section" className="space-y-4">
      <StatsHeader stats={stats} />

      <div className="flex gap-1 flex-wrap" data-testid="admin-ticket-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => { setStatus(t.id); setActiveId(null); }}
            data-testid={`admin-ticket-tab-${t.id}`}
            className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${
              status === t.id ? "btn-neon" : "btn-ghost"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search subject / body / player / id…"
          data-testid="admin-ticket-search"
          className="flex-1 min-w-[200px] bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="admin-ticket-category"
          className="bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>{c === "all" ? "All categories" : c}</option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="admin-ticket-sort"
          className="bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
        >
          {SORT_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className="grid lg:grid-cols-[minmax(280px,1fr)_2fr] gap-4">
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1"
             data-testid="admin-tickets-list">
          {data.tickets.length === 0 ? (
            <p className="text-[12px] text-white/45 text-center py-12"
               data-testid="admin-tickets-empty">
              No tickets in this bucket.
            </p>
          ) : data.tickets.map((t) => (
            <TicketRow
              key={t.ticket_id}
              ticket={t}
              active={activeId === t.ticket_id}
              onClick={() => setActiveId(t.ticket_id)}
            />
          ))}
        </div>

        <TicketDetailPane
          ticket={detail?.ticket}
          context={detail?.player_context}
          replyLog={detail?.reply_log}
          canned={canned}
          onReply={onReply}
          onStatus={onStatus}
          onClaim={onClaim}
          onRelease={onRelease}
          onReopen={onReopen}
          onSaveNote={onSaveNote}
          busy={busy}
          adminHandle={adminHandle}
          setAdminHandle={setAdminHandle}
        />
      </div>
    </div>
  );
}

export default TicketsSection;
